import { Alert, Card, Heading, NumberField } from "@/design-system";
import { BLOG_POSTS } from "../constants";
import { DelinquencyResult } from "../components/delinquencyResult";
import { RelatedLinks } from "../components/relatedLinks";
import { ToolPageLayout } from "../components/toolPageLayout";
import { ToolResultCta } from "../components/toolResultCta";
import { delinquencyLoss } from "../utils/finance";
import { useState } from "react";
import {
  DESCRIPTION,
  FAQ_ITEMS,
  FIDEXA_RECOVERY_RATE,
  TITLE,
  TRAIL,
  WEB_APPLICATION,
} from "./constants";

export const CalculadoraDeInadimplencia = () => {
  const [monthlySales, setMonthlySales] = useState<number | undefined>(80_000);
  const [delinquencyPercent, setDelinquencyPercent] = useState<
    number | undefined
  >(15);
  const [recoveryPercent, setRecoveryPercent] = useState<number | undefined>(
    10
  );
  const [stores, setStores] = useState<number | undefined>(1);

  const isReady = (monthlySales ?? 0) > 0 && (delinquencyPercent ?? 0) > 0;
  const storeCount = Math.max(stores ?? 1, 1);
  const result = isReady
    ? delinquencyLoss({
        monthlySales: monthlySales ?? 0,
        delinquencyRate: (delinquencyPercent ?? 0) / 100,
        recoveryRate: (recoveryPercent ?? 0) / 100,
        stores: storeCount,
        benchmarkRecoveryRate: FIDEXA_RECOVERY_RATE,
      })
    : null;

  return (
    <ToolPageLayout
      title={TITLE}
      description={DESCRIPTION}
      trail={TRAIL}
      schemas={[WEB_APPLICATION]}
      eyebrow="Calculadora de inadimplência"
      heading="Quanto sua loja perde com o crediário?"
      lead="Quatro números da sua operação mostram quanto do crediário próprio atrasa, quanto volta ao caixa e quanto fica na rua no fim do ano."
      faqItems={FAQ_ITEMS}
      faqHeading="Dúvidas sobre inadimplência no crediário"
    >
      {openDemo => (
        <>
          <section
            aria-labelledby="calculadora-title"
            className="mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-6 px-4 sm:px-6 lg:grid-cols-5 lg:px-8"
          >
            <Card className="flex min-w-0 flex-col gap-5 lg:col-span-2">
              <Heading level={2} size={4} id="calculadora-title">
                Sua operação
              </Heading>
              <NumberField
                label="Vendas no crediário por mês"
                required
                hint="Soma de todas as lojas, só o que foi vendido a prazo."
                prefix="R$"
                decimals={2}
                max={1_000_000_000}
                value={monthlySales}
                onValueChange={setMonthlySales}
              />
              <NumberField
                label="Parte do valor que atrasa"
                required
                hint="Ao começar numa loja, a Fidexa costuma encontrar entre 15% e 30%."
                suffix="%"
                decimals={2}
                max={100}
                value={delinquencyPercent}
                onValueChange={setDelinquencyPercent}
              />
              <NumberField
                label="Parte do atraso que você recupera hoje"
                required
                hint="Se não sabe, comece com 10%."
                suffix="%"
                decimals={2}
                max={100}
                value={recoveryPercent}
                onValueChange={setRecoveryPercent}
              />
              <NumberField
                label="Número de lojas"
                decimals={0}
                max={999}
                value={stores}
                onValueChange={setStores}
              />
            </Card>

            <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
              {result ? (
                <DelinquencyResult
                  result={result}
                  stores={storeCount}
                  benchmarkRecoveryRate={FIDEXA_RECOVERY_RATE}
                />
              ) : (
                <Alert tone="info" title="Complete os dados da operação">
                  Informe as vendas no crediário e a parte que atrasa para ver
                  quanto sua loja perde.
                </Alert>
              )}
              <ToolResultCta
                id="calculadora-cta-title"
                title="Veja esse número cair na sua carteira"
                description="Numa demonstração gratuita, um especialista mostra como a régua de cobrança da Fidexa traz de volta as parcelas em atraso pelo WhatsApp."
                onDemo={openDemo}
              />
            </div>
          </section>

          <RelatedLinks
            current="calculadora_inadimplencia"
            posts={[BLOG_POSTS.vazamentoInvisivel, BLOG_POSTS.cobrarIgual]}
          />
        </>
      )}
    </ToolPageLayout>
  );
};
